"use client";

import { deleteUserRole } from "@/features/privileges/user-roles/api/delete-user-role";
import { DataTable, DataTableColumnHeader } from "@/components/data-table";
import DeleteDialog from "@/components/delete-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EPermission } from "@/constants/permission.enum";
import { useDataTable } from "@/hooks/use-data-table";
import { serverActionMessage, withRBAC } from "@/lib/action-utils";
import { PermissionDeniedError } from "@/lib/errors";
import { formatDate, getBaseResourceUrl } from "@/lib/utils";
import { PagedList } from "@/types/paged-list-type";
import { IUserRole } from "@/features/privileges/roles/types";
import { ColumnDef } from "@tanstack/react-table";
import { EllipsisVertical } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import React, { Fragment, useState } from "react";
import { toast } from "sonner";

interface UserRoleDataTableProps {
  pagedList: PagedList<IUserRole>;
}

export function UserRoleDataTable({ pagedList }: UserRoleDataTableProps) {
  const t = useTranslations();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IUserRole | null>(null);

  const router = useRouter();
  const pathname = usePathname();

  const columns: ColumnDef<IUserRole>[] = [
    {
      accessorKey: "userName",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.user-role.user")}
        />
      ),
      cell: ({ row }) => row.original.userName,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "role.name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.user-role.role")}
        />
      ),
      cell: ({ row }) => row.original.role.name,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "createdDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.common.created-date")}
        />
      ),
      cell: ({ row }) => (
        <div className="leading-tight">
          <div>{formatDate(row.original.createdDate)}</div>
          <div className="text-xs text-muted-foreground">{row.original.createdByUser}</div>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "lastModifiedDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.common.last-modified-date")}
        />
      ),
      cell: ({ row }) => row.original.lastModifiedDate ? (
        <div className="leading-tight">
          <div>{formatDate(row.original.lastModifiedDate)}</div>
          <div className="text-xs text-muted-foreground">{row.original.modifiedByUser}</div>
        </div>
      ) : null,
      enableSorting: true,
    },
    {
      id: "actions",
      cell: function Cell({ row }) {
        const handleEdit = () => {
          router.push(`${pathname}/add-user-role/${row.original.id}`, {
            scroll: false,
          });
        };

        const handleDelete = () => {
          setIsOpenDialog(true);
          setSelectedRow(row.original);
        };

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span className="flex cursor-pointer items-center justify-center data-[state=open]:bg-muted">
                <EllipsisVertical className="size-4" aria-hidden="true" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onSelect={handleEdit}>
                {t("action.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDelete}>
                {t("action.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleDelete = async () => {
    try {
      if (selectedRow) {
        const protectedDelete = withRBAC({
          requiredPermissions: [EPermission.Delete],
          resourcePath: getBaseResourceUrl(pathname),
        })(async () => {
          return await deleteUserRole(selectedRow.id);
        });
        const response = await protectedDelete();

        serverActionMessage({
          response,
          successMessage: t("notification.deleted-successfully"),
          errorMessage: t("notification.update-failed"),
        });

        setIsOpenDialog(false);
        setSelectedRow(null);
      }
    } catch (error) {
      if (error instanceof PermissionDeniedError) {
        toast.error(error.message);
      } else {
        toast.error(t("notification.update-failed"));
      }
    }
  };

  const { table } = useDataTable({
    data: pagedList.results,
    columns,
    pageCount: pagedList.totalPages,
    defaultPerPage: pagedList.pageSize,
  });

  return (
    <Fragment>
      <DataTable table={table} />
      <DeleteDialog
        isOpen={isOpenDialog}
        setIsOpen={setIsOpenDialog}
        handleDelete={handleDelete}
      />
    </Fragment>
  );
}
